import React from 'react'
import { Box, Typography, Skeleton, useTheme, useMediaQuery } from '@mui/material'
import { Bolt as BoltIcon } from '@mui/icons-material'

interface AIVanityBannerProps {
  aiMessagesSent: number;
  loading?: boolean;
}

export const AIVanityBanner: React.FC<AIVanityBannerProps> = ({ aiMessagesSent, loading }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (loading) {
    return <Skeleton variant="rounded" height={80} sx={{ borderRadius: '16px', mb: 3.5 }} />
  }

  if (!aiMessagesSent) return null;

  const hoursSaved = Math.round((aiMessagesSent * 2) / 60);
  const minutesSaved = aiMessagesSent * 2;

  return (
    <Box sx={{ 
        mb: 3.5, 
        bgcolor: '#FFFFFF',
        border: '1px solid', 
        borderColor: 'rgba(63, 102, 187, 0.18)', 
        borderRadius: '16px', 
        p: { xs: 2, sm: 2.25 },
        px: { xs: 2, sm: 2.75 },
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 2, sm: 2.5 },
        boxShadow: '0 2px 8px -2px rgba(63, 102, 187, 0.06)',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: 'rgba(63, 102, 187, 0.35)',
          boxShadow: '0 4px 16px -4px rgba(63, 102, 187, 0.12)',
        }
      }}
    >
      {/* Left Metric Badge Box (Replaces generic AI icon) */}
      <Box sx={{
        background: 'linear-gradient(135deg, #3F66BB 0%, #34A1FB 100%)',
        color: '#FFFFFF',
        borderRadius: '14px',
        py: { xs: 1, sm: 1.25 },
        px: { xs: 1.5, sm: 2 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 4px 14px rgba(52, 161, 251, 0.3)',
        minWidth: { xs: 68, sm: 80 },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <BoltIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: '#FFD700' }} />
          <Typography variant="h6" sx={{ fontWeight: 900, fontSize: { xs: '1.1rem', sm: '1.25rem' }, lineHeight: 1 }}>
            {minutesSaved}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'rgba(255,255,255,0.9)', mt: 0.25, whiteSpace: 'nowrap' }}>
          Mins Saved
        </Typography>
      </Box>

      {/* Main Headline & Subtitle */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle1" sx={{ color: '#0F172A', fontWeight: 700, letterSpacing: '-0.3px', fontSize: { xs: '0.9rem', sm: '1.05rem' }, lineHeight: 1.3 }}>
          AI saved your business {hoursSaved} hours
        </Typography>
        {!isMobile && (
          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, display: 'block', mt: 0.25, fontSize: { xs: '0.75rem', sm: '0.82rem' } }}>
            Estimated from {aiMessagesSent.toLocaleString()} AI replies
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default AIVanityBanner
